import { defineStore } from 'pinia';
import camelcaseKeys from 'camelcase-keys';

import DocumentTypesAPI from 'dashboard/api/documentTypes';
import { throwErrorMessage } from 'dashboard/store/utils/api';

export const useDocumentTypesStore = defineStore('documentTypes', {
  state: () => ({
    records: [],
    loaded: false,
    uiFlags: {
      fetching: false,
      fetchError: false,
      creating: false,
      updating: false,
      deleting: false,
    },
  }),

  getters: {
    getDocumentTypes: state => camelcaseKeys(state.records, { deep: true }),
    getActiveDocumentTypes: state =>
      camelcaseKeys(
        state.records.filter(record => record.active),
        { deep: true }
      ),
    getUIFlags: state => state.uiFlags,
    hasLoaded: state => state.loaded,
  },

  actions: {
    setUIFlag(flags) {
      this.uiFlags = { ...this.uiFlags, ...flags };
    },

    async get({ force = false } = {}) {
      // Types change rarely; serve the cached list unless a refresh is forced.
      if (this.loaded && !force) return this.getDocumentTypes;
      this.setUIFlag({ fetching: true, fetchError: false });
      try {
        const { data } = await DocumentTypesAPI.get();
        this.records = data.payload || [];
        this.loaded = true;
        return this.getDocumentTypes;
      } catch (error) {
        this.setUIFlag({ fetchError: true });
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetching: false });
      }
    },

    async create(documentType) {
      this.setUIFlag({ creating: true });
      try {
        const { data } = await DocumentTypesAPI.create(documentType);
        this.records.push(data);
        return camelcaseKeys(data, { deep: true });
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creating: false });
      }
    },

    async update(id, documentType) {
      this.setUIFlag({ updating: true });
      try {
        const { data } = await DocumentTypesAPI.update(id, documentType);
        const index = this.records.findIndex(record => record.id === data.id);
        if (index !== -1) this.records[index] = data;
        return camelcaseKeys(data, { deep: true });
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ updating: false });
      }
    },

    async delete(id) {
      this.setUIFlag({ deleting: true });
      try {
        await DocumentTypesAPI.delete(id);
        // Unused types are destroyed, referenced ones soft-deactivated; both
        // return 200, so refetch to reflect whichever happened.
        await this.get({ force: true });
        return true;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ deleting: false });
      }
    },
  },
});
